<script setup lang="ts">
const { t: $t } = useI18n()

const props = defineProps<{
  id?: string
  accessCodeSession?: string
}>()
const emit = defineEmits<{ (e: 'close'): void }>()

const isAccessCodeSession = Boolean(props.accessCodeSession)

const { defectPublicQuery, commentQuery } = useCustomerModuleQuery()
const { data: defect } = defectPublicQuery.getById(computed(() => props.id))
const { data: comments } = commentQuery.getAll(computed(() => ({
  type: 'Defect',
  entityId: props.id,
})))

const isCommentAdded = ref(false)
watch(comments, (currentComments, prevComments) => {
  if (currentComments && prevComments && currentComments.length > prevComments.length) {
    isCommentAdded.value = true
  }
})

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const markAsRepaired = useMutation({
  mutationFn: $trpc.customerModule.defect.publicMarkAsRepaired.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('customerModule.defect.notification.markAsRepaired.error') }),
  onSuccess: async ({ id, offerId, machineryId }) => {
    const invalidateQueries = [
      queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'defects', 'getById', 'getCommentById', id] }),
    ]
    if (offerId) {
      invalidateQueries.push(queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'defects', 'getAllByEntityId', 'offer', offerId] }))
    }
    if (machineryId) {
      invalidateQueries.push(queryClient.invalidateQueries({ queryKey: [CUSTOMER_MODULE_QUERY_KEY, 'defects', 'getAllByEntityId', 'machinery', machineryId] }))
    }
    await Promise.all(invalidateQueries)

    notification.success({ title: $t('customerModule.defect.notification.markAsRepaired.success'), duration: 4500 })
    emit('close')
  },
})

const title = computed(() => {
  const id = defect.value ? defect.value[`${defect.value.type}Id`] : undefined
  return $t('customerModule.defect.repair.title', { id })
})
</script>

<template>
  <ThePopup :show="Boolean(defect)" :title @close="$emit('close')">
    <FormKit
      v-if="defect"
      type="form"
      :actions="false"
      @submit="markAsRepaired.mutate({ id: defect.id, accessCodeSession: props.accessCodeSession })"
    >
      <CustomerModuleCommentList
        v-if="!isAccessCodeSession"
        class="mb-4"
        type="Defect"
        :entity-id="defect.id"
        sort="desc"
        is-delete-disabled
        allow-access-all-public-comments
      />
      <p v-if="!isCommentAdded && !isAccessCodeSession" class="text-red-400 font-semibold mt-2">
        {{ $t('customerModule.defect.repair.submit.information') }}
      </p>
      <n-button type="primary" attr-type="submit" class="mt-4" :disabled="!isCommentAdded && !isAccessCodeSession">
        {{ $t('customerModule.defect.repair.submit.label') }}
      </n-button>
    </FormKit>
    <n-spin v-else />
  </ThePopup>
</template>
